import { simulationDocument } from './simulation'
import { holeById } from './board'
import { CD4017_CORE_TO_PHYSICAL_INDEX } from './cd4017'
import { CD4026_CORE_POST_LAYOUT } from './cd4026'
import { buildConnectivity, validateDocument } from './validation'
import {
  SEVEN_SEGMENT_COMMON_PHYSICAL_INDICES,
  SEVEN_SEGMENT_CORE_TO_PHYSICAL_INDEX,
} from './sevenSegment'
import type { BreadboardComponent, BreadboardDocument } from './types'

export interface ComponentBinding {
  componentId: string
  elementIndex: number
  expectedType: string
}

export interface NetlistBuildResult {
  circuit: string
  componentBindings: ComponentBinding[]
  contactControls: { componentId: string; sourceName: string }[]
  blocked: boolean
}

interface XY { x: number; y: number }

export function circuitElementType(component: Pick<BreadboardComponent, 'kind' | 'variant'>): string {
  switch (component.kind) {
    case 'resistor': return 'ResistorElm'
    case 'capacitor': return component.variant === 'electrolytic' ? 'PolarCapacitorElm' : 'CapacitorElm'
    case 'led': return 'LEDElm'
    case 'diode': return 'DiodeElm'
    case 'switch': return 'SwitchElm'
    case 'button': return 'SwitchElm'
    case 'npn':
    case 'pnp': return 'TransistorElm'
    case 'seven-segment': return 'SevenSegElm'
    case 'cd4017': return 'CD4017Elm'
    case 'cd4026': return 'CD4026Elm'
    case 'buzzer':
    case 'esp32-s3': return '' // Visual-only board; never exported to CircuitJS.
  }
}

function ledRgb(color: string | undefined): [number, number, number] {
  const match = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color ?? '')
  if (!match) return [1, 0, 0]
  return [1, 2, 3].map((index) => Number.parseInt(match[index] as string, 16) / 255) as [number, number, number]
}

export function buildCircuitJsNetlist(
  inputDocument: BreadboardDocument,
  closedContacts: Readonly<Record<string, boolean>> = {},
): NetlistBuildResult {
  const document = simulationDocument(inputDocument)
  const issues = validateDocument(document)
  if (issues.some((issue) => issue.level === 'error')) {
    return { circuit: '', componentBindings: [], contactControls: [], blocked: true }
  }

  const connectivity = buildConnectivity(document)
  const roots = Array.from(new Set(connectivity.rootForHole.values())).sort()
  const points = new Map<string, XY>()
  roots.forEach((root, index) => {
    points.set(root, { x: 80 + (index % 36) * 32, y: 128 + Math.floor(index / 36) * 32 })
  })

  const lines = ['$ 1 0.000005 10.20027730826997 50 5 50 5e-11']
  let elementCount = 0
  const addElement = (line: string): number => {
    const elementIndex = elementCount
    elementCount += 1
    lines.push(line)
    return elementIndex
  }
  const sourcePositive = { x: 32, y: 48 }
  const sourceGround = { x: 32, y: 80 }
  // CircuitJS defines the second post as V(first) + source voltage.
  addElement(`v ${sourceGround.x} ${sourceGround.y} ${sourcePositive.x} ${sourcePositive.y} 0 0 40 5 0 0 0.5`)
  addElement(`g ${sourceGround.x} ${sourceGround.y} ${sourceGround.x} ${sourceGround.y + 16} 0`)

  const wireTo = (from: XY, to: XY) => addElement(`w ${from.x} ${from.y} ${to.x} ${to.y} 0`)
  for (const nodeId of ['rail-top-positive', 'rail-bottom-positive']) {
    const root = connectivity.rootForNode.get(nodeId)
    const target = root ? points.get(root) : undefined
    if (target) wireTo(sourcePositive, target)
  }
  for (const nodeId of ['rail-top-negative', 'rail-bottom-negative']) {
    const root = connectivity.rootForNode.get(nodeId)
    const target = root ? points.get(root) : undefined
    if (target) wireTo(sourceGround, target)
  }

  const componentBindings: ComponentBinding[] = []
  const contactControls: NetlistBuildResult['contactControls'] = []
  const liveContacts = document.components.some((component) => component.kind === 'cd4017' || component.kind === 'cd4026')
  document.components.forEach((component, index) => {
    const pinPoints = component.pins.map((pin) => {
      const root = connectivity.rootForHole.get(pin)
      return root ? points.get(root) : undefined
    })
    if (pinPoints.some((point) => !point)) return

    const origin = { x: 80 + (index % 12) * 96, y: 480 + Math.floor(index / 12) * 80 }
    if (component.kind === 'seven-segment') {
      // With a decimal point, SevenSegElm places a-d/common on the left and
      // e-g/dp on the right (the common moves left when segmentCount is 8).
      // A standard-size ChipElm uses a 32px post pitch and a 192px span.
      const displayOrigin = {
        x: 1600 + (index % 4) * 288,
        y: 480 + Math.floor(index / 4) * 192,
      }
      const leftPosts = Array.from({ length: 4 }, (_, pin) => ({
        x: displayOrigin.x,
        y: displayOrigin.y + pin * 32,
      }))
      const rightPosts = Array.from({ length: 4 }, (_, pin) => ({
        x: displayOrigin.x + 192,
        y: displayOrigin.y + pin * 32,
      }))
      const commonPost = { x: displayOrigin.x, y: displayOrigin.y + 128 }
      const corePosts = [...leftPosts, ...rightPosts, commonPost]
      SEVEN_SEGMENT_CORE_TO_PHYSICAL_INDEX.forEach((physicalIndex, coreIndex) => {
        wireTo(pinPoints[physicalIndex] as XY, corePosts[coreIndex] as XY)
      })
      for (const physicalIndex of SEVEN_SEGMENT_COMMON_PHYSICAL_INDICES) {
        wireTo(pinPoints[physicalIndex] as XY, corePosts[8] as XY)
      }
      const diodeDirection = component.variant === 'common-anode' ? -1 : 1
      componentBindings.push({
        componentId: component.id,
        elementIndex: addElement(`157 ${displayOrigin.x} ${displayOrigin.y} ${displayOrigin.x + 128} ${displayOrigin.y} 0 7 1 ${diodeDirection}`),
        expectedType: circuitElementType(component),
      })
    } else if (component.kind === 'cd4017') {
      // Keep the tall 16-post chip separate from the display and discrete layouts.
      // CD4017Elm uses six west posts and ten east posts, spaced by 32px.
      const chipOrigin = { x: 3000 + (index % 4) * 256, y: 480 + Math.floor(index / 4) * 384 }
      CD4017_CORE_TO_PHYSICAL_INDEX.forEach((physicalIndex, coreIndex) => {
        wireTo(pinPoints[physicalIndex] as XY, {
          x: chipOrigin.x + (coreIndex < 6 ? 0 : 128),
          y: chipOrigin.y + (coreIndex < 6 ? coreIndex : coreIndex - 6) * 32,
        })
      })
      componentBindings.push({
        componentId: component.id,
        elementIndex: addElement(`4017 ${chipOrigin.x} ${chipOrigin.y} ${chipOrigin.x + 128} ${chipOrigin.y} 2 0 false false`),
        expectedType: circuitElementType(component),
      })
    } else if (component.kind === 'cd4026') {
      // CD4026Elm keeps its core terminals in physical 1–16 order, while the
      // drawing distributes them between two eight-position sides.
      const chipOrigin = { x: 4200 + (index % 4) * 256, y: 480 + Math.floor(index / 4) * 320 }
      CD4026_CORE_POST_LAYOUT.forEach((layout, physicalIndex) => {
        wireTo(pinPoints[physicalIndex] as XY, {
          x: chipOrigin.x + (layout.side === 'right' ? 128 : 0),
          y: chipOrigin.y + layout.position * 32,
        })
      })
      componentBindings.push({
        componentId: component.id,
        elementIndex: addElement(`4026 ${chipOrigin.x} ${chipOrigin.y} ${chipOrigin.x + 112} ${chipOrigin.y} 0 0 false false false true`),
        expectedType: circuitElementType(component),
      })
    } else if (component.kind === 'npn' || component.kind === 'pnp') {
      // Breadboard transistor pins are stored in physical left-to-right E-B-C
      // order. CircuitJS TransistorElm requires node order B-C-E.
      const emitterPin = pinPoints[0] as XY
      const basePin = pinPoints[1] as XY
      const collectorPin = pinPoints[2] as XY
      const base = origin
      const body = { x: origin.x + 32, y: origin.y }
      const pnp = component.kind === 'pnp' ? -1 : 1
      const collector = { x: body.x, y: body.y - 16 * pnp }
      const emitter = { x: body.x, y: body.y + 16 * pnp }
      wireTo(basePin, base)
      wireTo(collectorPin, collector)
      wireTo(emitterPin, emitter)
      const elementIndex = addElement(`t ${base.x} ${base.y} ${body.x} ${body.y} 0 ${pnp} 0 0 ${component.value} default`)
      componentBindings.push({ componentId: component.id, elementIndex, expectedType: circuitElementType(component) })
    } else {
      const end = { x: origin.x + 64, y: origin.y }
      wireTo(pinPoints[0] as XY, origin)
      wireTo(pinPoints[1] as XY, end)
      switch (component.kind) {
        case 'resistor':
          componentBindings.push({
            componentId: component.id,
            elementIndex: addElement(`r ${origin.x} ${origin.y} ${end.x} ${end.y} 0 ${component.value}`),
            expectedType: circuitElementType(component),
          })
          break
        case 'capacitor':
          componentBindings.push({
            componentId: component.id,
            elementIndex: component.variant === 'electrolytic'
              ? addElement(`209 ${origin.x} ${origin.y} ${end.x} ${end.y} 0 ${component.value} 0 0.001 1`)
              : addElement(`c ${origin.x} ${origin.y} ${end.x} ${end.y} 0 ${component.value} 0 0.001`),
            expectedType: circuitElementType(component),
          })
          break
        case 'led': {
          const [red, green, blue] = ledRgb(component.color)
          componentBindings.push({
            componentId: component.id,
            elementIndex: addElement(`162 ${origin.x} ${origin.y} ${end.x} ${end.y} 2 default-led ${red} ${green} ${blue} ${component.value}`),
            expectedType: circuitElementType(component),
          })
          break
        }
        case 'diode':
          componentBindings.push({
            componentId: component.id,
            elementIndex: addElement(`d ${origin.x} ${origin.y} ${end.x} ${end.y} 2 default`),
            expectedType: circuitElementType(component),
          })
          break
        case 'switch':
        case 'button':
          if (liveContacts) {
            // Reuse the existing external-voltage API for live contacts, so a
            // manual clock edge does not re-import and reset the counter circuit.
            const control = { x: origin.x + 32, y: origin.y + 16 }
            const sourceName = `bb-contact-${index}`
            addElement(`418 ${control.x} ${control.y} ${control.x} ${control.y + 32} 0 0 40 5 0 0 0.5 ${sourceName}`)
            componentBindings.push({
              componentId: component.id,
              elementIndex: addElement(`159 ${origin.x} ${origin.y} ${end.x} ${end.y} 0 0.001 1e10 2.5`),
              expectedType: 'AnalogSwitchElm',
            })
            contactControls.push({ componentId: component.id, sourceName })
            break
          }
          // CircuitJS switch position: 0 = closed, 1 = open. The workbench owns
          // the momentary interaction, so the imported switch itself is not toggled.
          componentBindings.push({
            componentId: component.id,
            elementIndex: addElement(`s ${origin.x} ${origin.y} ${end.x} ${end.y} 0 ${closedContacts[component.id] ? 0 : 1} false`),
            expectedType: circuitElementType(component),
          })
          break
      }
    }
  })

  return { circuit: lines.join('\n'), componentBindings, contactControls, blocked: false }
}

export function netForHole(document: BreadboardDocument, holeId: string): string | undefined {
  if (!holeById.has(holeId)) return undefined
  return buildConnectivity(document).rootForHole.get(holeId)
}
